import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Trash2,
  PlaneLanding,
  PlaneTakeoff,
  AlertTriangle,
  Pencil,
  ChevronDown,
  ChevronUp,
  DoorClosed,
} from "lucide-react";
import { formatDMY, todayISO } from "@/lib/dateUtils";
import { cn } from "@/lib/utils";
import { useT } from "@/lib/i18n";

const COLLAPSED_LIMIT = 4;

export default function TripsList({ trips, result, onDelete, onEdit, onClose, loading }) {
  const { t } = useT();
  const [expanded, setExpanded] = useState(false);

  const usageMap = useMemo(
    () => new Map((result?.perTripUsage || []).map((u) => [u.id, u])),
    [result],
  );
  const overlapIds = useMemo(
    () => new Set((result?.overlaps || []).flat()),
    [result],
  );

  const sortedTrips = useMemo(
    () =>
      (trips || [])
        .slice()
        .sort((a, b) => (a.entry < b.entry ? 1 : a.entry > b.entry ? -1 : 0)),
    [trips],
  );

  const totalTrips = sortedTrips.length;
  const shouldCollapse = !expanded && totalTrips > COLLAPSED_LIMIT;
  const visibleTrips = shouldCollapse
    ? sortedTrips.slice(0, COLLAPSED_LIMIT)
    : sortedTrips;
  const hiddenCount = totalTrips - COLLAPSED_LIMIT;

  return (
    <Card
      className="bg-[#131620] border-[#272A35] rounded-xl p-5 md:p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] w-full h-full flex flex-col"
      data-testid="trips-list"
    >
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-serif-display text-xl text-white tracking-tight">
          {t("trips.title")}
        </h3>
        <span className="text-xs text-slate-400 font-mono-terminal">
          {totalTrips}{" "}
          {totalTrips === 1 ? t("trips.count.one") : t("trips.count.many")}
        </span>
      </div>

      {totalTrips > 0 && !loading && (
        <div
          className="mb-3 flex items-center gap-4 flex-wrap text-[11px] text-slate-400"
          data-testid="trips-legend"
        >
          <div className="flex items-center gap-1.5">
            <span className="h-3 w-1 rounded-full bg-[#D4AF37] shrink-0" />
            <span>{t("trips.legend.inWindow")}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-3 w-1 rounded-full bg-slate-700 shrink-0" />
            <span>{t("trips.legend.outOfWindow")}</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center text-sm text-slate-400 flex-1 flex items-center justify-center">
          {t("trips.loading")}
        </div>
      ) : totalTrips === 0 ? (
        <div className="py-10 text-center border border-dashed border-[#272A35] rounded-lg flex-1 flex flex-col items-center justify-center">
          <p className="text-sm text-slate-400">{t("trips.empty.title")}</p>
          <p className="text-xs text-slate-500 mt-1">{t("trips.empty.sub")}</p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          <ul
            className={cn(
              "space-y-2 flex-1",
              expanded && totalTrips > COLLAPSED_LIMIT
                ? "overflow-y-auto pr-1 -mr-1"
                : "",
            )}
            style={
              expanded && totalTrips > COLLAPSED_LIMIT
                ? { maxHeight: "560px" }
                : undefined
            }
            data-testid="trips-list-items"
          >
            {visibleTrips.map((trip) => {
              const usage = usageMap.get(trip.id);
              const inWindow = usage && usage.daysCounted > 0;
              const isOverlap = overlapIds.has(trip.id);
              return (
                <li
                  key={trip.id}
                  className={cn(
                    "flex items-center justify-between gap-3 p-3 rounded-lg border transition-colors",
                    "border-[#272A35] bg-[#0C0E14]/60 hover:bg-[#1A1E2C]",
                  )}
                  data-testid={`trip-item-${trip.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div
                      className={cn(
                        "h-8 w-1 rounded-full shrink-0",
                        inWindow ? "bg-[#D4AF37]" : "bg-slate-700",
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm font-mono-terminal text-slate-100 flex-wrap">
                        <PlaneLanding className="h-3.5 w-3.5 text-emerald-400/80 shrink-0" />
                        <span>{formatDMY(trip.entry)}</span>
                        <span className="text-slate-600">→</span>
                        <PlaneTakeoff className="h-3.5 w-3.5 text-sky-400/80 shrink-0" />
                        <span>
                          {trip.exit ? (
                            formatDMY(trip.exit)
                          ) : (
                            <span className="text-amber-400">
                              {t("trips.ongoing")}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="text-xs text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
                        <span>
                          {t("trips.totalPrefix")}{" "}
                          {usage ? usage.totalDays : "-"}{" "}
                          {usage?.totalDays === 1
                            ? t("trips.days.one")
                            : t("trips.days.many")}
                        </span>
                        <span className="text-slate-600">·</span>
                        <span>
                          {t("trips.inWindow")}{" "}
                          <span
                            className={cn(
                              "font-medium",
                              inWindow ? "text-[#D4AF37]" : "text-slate-500",
                            )}
                          >
                            {usage ? usage.daysCounted : 0}
                          </span>
                        </span>
                        {isOverlap && (
                          <Badge
                            variant="outline"
                            className="border-amber-500/40 text-amber-300 bg-amber-500/10 h-5 px-1.5 text-[10px] gap-1"
                          >
                            <AlertTriangle className="h-3 w-3" />
                            {t("trips.overlap")}
                          </Badge>
                        )}
                        {!trip.exit && (
                          <Badge
                            variant="outline"
                            className="border-sky-500/40 text-sky-300 bg-sky-500/10 h-5 px-1.5 text-[10px]"
                          >
                            {t("trips.open")}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit?.(trip)}
                    className="text-slate-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 shrink-0"
                    data-testid={`trip-edit-${trip.id}`}
                    aria-label="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {!trip.exit && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onClose?.(trip, todayISO())}
                      className="text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 shrink-0"
                      data-testid={`trip-close-${trip.id}`}
                      aria-label={t("trips.closeTrip")}
                      title={t("trips.closeTrip")}
                    >
                      <DoorClosed className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(trip.id)}
                    className="text-slate-400 hover:text-red-400 hover:bg-red-500/10 shrink-0"
                    data-testid={`trip-delete-${trip.id}`}
                    aria-label="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              );
            })}
          </ul>

          {totalTrips > COLLAPSED_LIMIT && (
            <div className="mt-3 pt-3 border-t border-[#272A35] flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded((v) => !v)}
                className="text-slate-300 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8"
                data-testid="trips-list-toggle"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1.5" />
                    {t("trips.showLess")}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1.5" />
                    {t("trips.showMore", { n: hiddenCount })}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
